import { zodResolver } from "@hookform/resolvers/zod";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import {
  createManualSyncSchema,
  getManualSyncBounds,
  getManualSyncRange,
  MANUAL_SYNC_PRESETS,
  type ManualSyncInput,
} from "@/lib/manual-sync-range";

export interface ManualSyncShopOption {
  shopId: string;
  label: string;
  timeZone: string;
  disabled?: boolean;
}

export interface ManualSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shops: readonly ManualSyncShopOption[];
  isSubmitting: boolean;
  onSubmit: (input: ManualSyncInput) => Promise<void>;
  submitError?: string;
}

export function ManualSyncDialog({
  open,
  onOpenChange,
  shops,
  isSubmitting,
  onSubmit,
  submitError,
}: ManualSyncDialogProps) {
  const { t } = useTranslation("settings");
  const id = useId();
  const [shopPickerOpen, setShopPickerOpen] = useState(false);
  const [openedAt, setOpenedAt] = useState(() => new Date());
  const [requestFailed, setRequestFailed] = useState(false);
  const [requestPending, setRequestPending] = useState(false);
  const wasOpen = useRef(false);
  const submitting = useRef(false);
  const enabledIds = shops.filter((shop) => !shop.disabled).map((shop) => shop.shopId);
  const enabledZones = shops.filter((shop) => !shop.disabled).map((shop) => shop.timeZone);
  const [selectedZones, setSelectedZones] = useState<readonly string[]>(
    enabledZones.length ? enabledZones : ["UTC"],
  );
  const form = useForm<ManualSyncInput>({
    resolver: zodResolver(createManualSyncSchema(enabledIds, openedAt, selectedZones)),
    defaultValues: {
      shopIds: enabledIds,
      ...getManualSyncRange(3, openedAt, enabledZones.length ? enabledZones : ["UTC"]),
    },
  });
  const { reset } = form;
  useEffect(() => {
    if (open && !wasOpen.current) {
      const now = new Date();
      setOpenedAt(now);
      setShopPickerOpen(false);
      setRequestFailed(false);
      const selected = shops.filter((shop) => !shop.disabled);
      const zones = selected.map((shop) => shop.timeZone);
      setSelectedZones(zones.length ? zones : ["UTC"]);
      reset({
        shopIds: selected.map((shop) => shop.shopId),
        ...getManualSyncRange(3, now, zones.length ? zones : ["UTC"]),
      });
    }
    wasOpen.current = open;
  }, [open, reset, shops]);

  const values = form.watch();
  const busy = isSubmitting || requestPending || form.formState.isSubmitting;
  const allSelected =
    enabledIds.length > 0 && enabledIds.every((shopId) => values.shopIds.includes(shopId));
  const bounds = getManualSyncBounds(openedAt, selectedZones);
  const selectedShopKey = values.shopIds.join("|");
  useEffect(() => {
    const ids = selectedShopKey ? selectedShopKey.split("|") : [];
    const enabled = new Set(shops.filter((shop) => !shop.disabled).map((shop) => shop.shopId));
    const retained = ids.filter((id) => enabled.has(id));
    if (retained.length !== ids.length)
      form.setValue("shopIds", retained, { shouldValidate: true });
    const zones = shops
      .filter((shop) => retained.includes(shop.shopId) && !shop.disabled)
      .map((shop) => shop.timeZone);
    const nextZones = zones.length ? zones : ["UTC"];
    setSelectedZones(nextZones);
    const next = getManualSyncBounds(openedAt, nextZones);
    const currentStart = form.getValues("startDate");
    const currentEnd = form.getValues("endDate");
    const end =
      currentEnd < next.minDate
        ? next.minDate
        : currentEnd > next.maxDate
          ? next.maxDate
          : currentEnd;
    const start =
      currentStart < next.minDate ? next.minDate : currentStart > end ? end : currentStart;
    if (start !== currentStart) form.setValue("startDate", start);
    if (end !== currentEnd) form.setValue("endDate", end);
  }, [selectedShopKey, shops, openedAt, form]);
  const errorMessage = (message: string | undefined) => {
    switch (message) {
      case "selectShop":
        return t("manualSync.validation.selectShop");
      case "unavailableShop":
        return t("manualSync.validation.unavailableShop");
      case "dateOrder":
        return t("manualSync.validation.dateOrder");
      case "dateWindow":
        return t("manualSync.validation.dateWindow");
      default:
        return t("manualSync.validation.invalidDate");
    }
  };
  const submit = form.handleSubmit(async (input) => {
    if (submitting.current || isSubmitting) return;
    submitting.current = true;
    setRequestPending(true);
    setRequestFailed(false);
    try {
      await onSubmit(input);
    } catch {
      // The parent supplies the API error; preserve the user's selection for retry.
      setRequestFailed(true);
    } finally {
      submitting.current = false;
      setRequestPending(false);
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!busy && !submitting.current) onOpenChange(nextOpen);
      }}
    >
      <DialogPopup
        className="sm:max-w-xl"
        closeProps={{ disabled: busy, "aria-label": t("manualSync.close") }}
      >
        <DialogHeader>
          <DialogTitle>{t("manualSync.title")}</DialogTitle>
          <DialogDescription>{t("manualSync.description")}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={submit} className="flex min-h-0 flex-col">
          <div className="flex flex-col gap-6 overflow-y-auto px-6 pb-6">
            <FieldSet
              disabled={busy}
              aria-describedby={form.formState.errors.shopIds ? `${id}-shops-error` : undefined}
            >
              <FieldLegend id={`${id}-shops-label`} variant="label" className="mb-0 w-full">
                <span className="flex items-center justify-between gap-2">
                  <span>{t("manualSync.shops")}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {t("manualSync.selected", { count: values.shopIds.length })}
                  </span>
                </span>
              </FieldLegend>
              {shops.length > 0 ? (
                <Popover open={open && shopPickerOpen && !busy} onOpenChange={setShopPickerOpen}>
                  <PopoverTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between font-normal"
                      />
                    }
                    disabled={busy || enabledIds.length === 0}
                    aria-labelledby={`${id}-shops-label ${id}-shops-value`}
                    aria-invalid={Boolean(form.formState.errors.shopIds)}
                    aria-describedby={
                      form.formState.errors.shopIds ? `${id}-shops-error` : undefined
                    }
                  >
                    <span id={`${id}-shops-value`} className="min-w-0 truncate">
                      {values.shopIds.length === 0
                        ? t("manualSync.shopPlaceholder")
                        : values.shopIds.length === 1
                          ? shops.find((shop) => shop.shopId === values.shopIds[0])?.label
                          : t("manualSync.selected", { count: values.shopIds.length })}
                    </span>
                    <IconChevronDown
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                  </PopoverTrigger>
                  <PopoverPopup
                    align="start"
                    className="w-(--anchor-width)"
                    aria-label={t("manualSync.shops")}
                  >
                    <div className="overflow-hidden">
                      <Label className="flex cursor-pointer items-center gap-3 border-b bg-muted/30 px-3 py-3">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={!allSelected && values.shopIds.length > 0}
                          disabled={busy || enabledIds.length === 0}
                          onCheckedChange={(checked) =>
                            form.setValue("shopIds", checked ? enabledIds : [], {
                              shouldValidate: true,
                            })
                          }
                        />
                        {t("manualSync.selectAll")}
                      </Label>
                      <div className="max-h-48 overflow-y-auto divide-y">
                        {shops.map((shop) => (
                          <Label
                            key={shop.shopId}
                            className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-3 has-disabled:cursor-default has-disabled:text-muted-foreground"
                          >
                            <Checkbox
                              checked={values.shopIds.includes(shop.shopId)}
                              disabled={busy || shop.disabled}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  "shopIds",
                                  checked
                                    ? [...values.shopIds, shop.shopId]
                                    : values.shopIds.filter((shopId) => shopId !== shop.shopId),
                                  { shouldValidate: true },
                                )
                              }
                            />
                            <span className="min-w-0 flex-1">{shop.label}</span>
                            {shop.disabled ? (
                              <span className="text-xs font-normal">
                                {t("manualSync.unavailable")}
                              </span>
                            ) : null}
                          </Label>
                        ))}
                      </div>
                    </div>
                  </PopoverPopup>
                </Popover>
              ) : (
                <p className="text-sm text-muted-foreground">{t("manualSync.noShops")}</p>
              )}
              <div className="min-h-4">
                {form.formState.errors.shopIds ? (
                  <p
                    id={`${id}-shops-error`}
                    role="alert"
                    className="text-xs text-destructive-foreground"
                  >
                    {errorMessage(form.formState.errors.shopIds.message)}
                  </p>
                ) : null}
              </div>
            </FieldSet>
            <FieldSet disabled={busy}>
              <FieldLegend variant="label">{t("manualSync.dateRange")}</FieldLegend>
              <div className="grid grid-cols-4 gap-2">
                {MANUAL_SYNC_PRESETS.map((days) => {
                  const range = getManualSyncRange(days, openedAt, selectedZones);
                  const selected =
                    values.startDate === range.startDate && values.endDate === range.endDate;
                  return (
                    <Button
                      key={days}
                      type="button"
                      variant={selected ? "secondary" : "outline"}
                      size="sm"
                      aria-pressed={selected}
                      disabled={busy}
                      onClick={() => {
                        form.setValue("startDate", range.startDate);
                        form.setValue("endDate", range.endDate);
                        void form.trigger(["startDate", "endDate"]);
                      }}
                    >
                      {t("manualSync.preset", { days })}
                    </Button>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(["startDate", "endDate"] as const).map((field) => (
                  <Field key={field} invalid={Boolean(form.formState.errors[field])}>
                    <FieldLabel htmlFor={`${id}-${field}`}>{t(`manualSync.${field}`)}</FieldLabel>
                    <Input
                      nativeInput
                      id={`${id}-${field}`}
                      type="date"
                      min={bounds.minDate}
                      max={bounds.maxDate}
                      disabled={busy}
                      aria-invalid={Boolean(form.formState.errors[field])}
                      aria-describedby={
                        form.formState.errors[field] ? `${id}-${field}-error` : undefined
                      }
                      {...form.register(field)}
                    />
                    {form.formState.errors[field] ? (
                      <FieldError match id={`${id}-${field}-error`}>
                        {errorMessage(form.formState.errors[field]?.message)}
                      </FieldError>
                    ) : null}
                  </Field>
                ))}
              </div>
            </FieldSet>
            {submitError || requestFailed ? (
              <p role="alert" className="text-sm text-destructive-foreground">
                {submitError || t("manualSync.submitFailed")}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t("manualSync.cancel")}
            </Button>
            <Button type="submit" loading={busy} disabled={busy || enabledIds.length === 0}>
              {busy ? t("manualSync.submitting") : t("manualSync.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
